/**
 * Cross-Module Client (Phase 4 — #207)
 *
 * A dual-mode abstraction for calling functions exposed by another module's
 * `services/public-api.ts`. Supports two transports transparently:
 *
 *   1. **Bundled mode (default)** — the target module is in the same worker
 *      bundle. Calls go through direct imports (zero overhead, identical to
 *      `import { createInvoice } from '../../accounting/services/public-api'`).
 *
 *   2. **RPC mode** — the target module runs as an independent Cloudflare
 *      Worker accessed via a Service Binding (`env.MOD_ACCOUNTING`). Calls
 *      are forwarded to the target worker's WorkerEntrypoint class via
 *      Cloudflare Workers RPC. D1Database references cross the service
 *      boundary transparently (zero-copy within the same colo).
 *
 * The abstraction is **additive**: existing direct imports continue to work.
 * Callers opt into the abstraction by calling `createCrossModuleClient(env)`
 * (or the `useCrossModule(env)` alias). This lets us migrate call sites
 * incrementally without a flag-day rewrite.
 *
 * ---------------------------------------------------------------------------
 * Usage
 * ---------------------------------------------------------------------------
 *
 * ```ts
 * import { useCrossModule } from '@kubdee/sdk';
 *
 * async function handler(c) {
 *   const cross = useCrossModule(c.env);
 *   await cross.accounting.createInvoice(db, companyId, data);
 * }
 * ```
 *
 * In bundled mode: `cross.accounting` is the local `public-api` namespace.
 * In RPC mode:     `cross.accounting` is a proxy around `env.MOD_ACCOUNTING`.
 *
 * ---------------------------------------------------------------------------
 * How RPC methods are exposed
 * ---------------------------------------------------------------------------
 *
 * Each module-worker's `WorkerEntrypoint` class forwards every exported
 * function from its `public-api` as an RPC method (see
 * `apps/module-worker/src/create-module-worker.ts`). Cloudflare Workers RPC
 * marshals arguments transparently — including `D1Database` references,
 * which are proxied rather than serialised.
 */

import {
  generatedModuleTransports,
  type GeneratedCrossModuleClient,
  type GeneratedCrossModuleEnv,
  type GeneratedModuleTransportConfig,
} from "./generated/cross-module-public-apis";

type AccountingApi = typeof import("../../../modules/accounting/services/public-api");
type WorkflowApi = typeof import("../../../modules/core/services/workflow/public-api");
type ChatApi = typeof import("../../../modules/chat/services/public-api");
type AiApi = typeof import("../../../modules/ai/services/public-api");
type CarRentalApi = typeof import("../../../modules/car-rental/services/public-api");
type InventoryApi = typeof import("../../../modules/inventory/services/public-api");

/**
 * The full cross-module surface — one namespace per module that exposes a
 * `public-api`. Consumers access functions as `client.<module>.<function>(...)`.
 *
 * Only modules that currently export a `services/public-api.ts` are listed;
 * new entries should be added as modules gain cross-module surfaces.
 */
export type CrossModuleClient = GeneratedCrossModuleClient & {
  accounting: AccountingApi;
  workflow: WorkflowApi;
  chat: ChatApi;
  ai: AiApi;
  carRental: CarRentalApi;
  inventory: InventoryApi;
};

// ---------------------------------------------------------------------------
// Service binding shape
// ---------------------------------------------------------------------------

/**
 * Minimal env shape the client needs to inspect. In practice this is
 * `ModuleWorkerEnv` (for module workers) or `TenantWorkerVariables['env']`
 * (for the tenant worker) — but we keep the type loose so the SDK does
 * not need to depend on those worker-specific types.
 *
 * Any missing binding falls back to the direct-import implementation.
 */
export interface CrossModuleEnv extends GeneratedCrossModuleEnv {}

// ---------------------------------------------------------------------------
// Transport: RPC proxy
// ---------------------------------------------------------------------------

/**
 * Wrap a Cloudflare Service binding as a namespace where every property
 * access returns a function that forwards to the binding's RPC method.
 *
 * Arguments are passed through unchanged — Cloudflare Workers RPC marshals
 * `D1Database`, `Request`, plain values, etc. automatically.
 */
function rpcProxy<T extends object>(binding: any): T {
  return new Proxy({} as T, {
    get(_target, prop: string | symbol) {
      // Protect against accidental enumeration / JSON.stringify / await
      if (typeof prop !== "string") return undefined;
      if (prop === "then") return undefined; // avoid accidental "thenable" detection

      // Return a function that forwards to the RPC binding.
      return (...args: unknown[]) => {
        const method = (binding as Record<string, unknown>)[prop];
        if (typeof method !== "function") {
          throw new Error(
            `[cross-module-client] RPC method not exposed on service binding: ${prop}`,
          );
        }
        return (method as (...a: unknown[]) => unknown).apply(binding, args);
      };
    },
  });
}

// ---------------------------------------------------------------------------
// Transport: direct imports (fallback)
// ---------------------------------------------------------------------------
//
// We load the local public-api modules lazily so that:
//   1. This file stays tree-shakeable in RPC-only builds (module workers
//      that only need the client for OUTGOING calls to other modules can
//      skip the direct-import bundle altogether).
//   2. There's no import cycle at module load time.
//
// If a module isn't available at runtime (e.g. it wasn't bundled), the
// `require()` will throw — but that's the same failure mode as today's
// direct imports, so the behaviour is preserved.

type Loader<T> = () => Promise<T> | T;

async function loadOrThrow<T>(name: string, loader: Loader<T>): Promise<T> {
  try {
    return await loader();
  } catch (err) {
    throw new Error(
      `[cross-module-client] Failed to load bundled public-api for "${name}". ` +
        `Either bundle the module or provide env.MOD_${name.toUpperCase()} as a Service Binding. ` +
        `Original: ${(err as Error).message}`,
    );
  }
}

/**
 * Build a namespace that calls the local public-api via dynamic import.
 *
 * We use dynamic `import()` so the loader only pulls in the module when a
 * method is actually invoked. Each call awaits the cached import promise.
 */
function directProxy<T extends object>(name: string, loader: Loader<T>): T {
  let cached: Promise<T> | undefined;
  const load = () => (cached ??= Promise.resolve(loadOrThrow(name, loader)));

  return new Proxy({} as T, {
    get(_target, prop: string | symbol) {
      if (typeof prop !== "string") return undefined;
      if (prop === "then") return undefined;

      return async (...args: unknown[]) => {
        const mod = await load();
        const fn = (mod as Record<string, unknown>)[prop];
        if (typeof fn !== "function") {
          throw new Error(
            `[cross-module-client] Function "${prop}" not exported by ${name}/services/public-api`,
          );
        }
        return (fn as (...a: unknown[]) => unknown)(...args);
      };
    },
  });
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Per-module configuration: the service-binding key + the direct-import
 * loader to use when the binding isn't available.
 */
interface ModuleTransportConfig<T extends object>
  extends GeneratedModuleTransportConfig<T> {
  loadBundled: Loader<T>;
}

// The direct-import loaders are generated so modules with a public API surface
// only need `services/public-api.ts` + `pnpm gen:modules` to become available.
const MODULE_TRANSPORTS = generatedModuleTransports;

/**
 * Create a cross-module client for the current worker `env`.
 *
 * For each module namespace we prefer the RPC transport (Service Binding)
 * when available, and fall back to the bundled direct-import transport
 * otherwise. The same object is returned for both modes so callers can
 * use one API regardless of deployment shape.
 *
 * @param env - Worker env (or any object with optional `MOD_*` bindings)
 */
export function createCrossModuleClient(
  env: CrossModuleEnv | undefined | null,
): CrossModuleClient {
  const safeEnv: CrossModuleEnv = env ?? {};

  function pick<T extends object>(
    name: string,
    cfg: ModuleTransportConfig<T>,
  ): T {
    const binding = safeEnv[cfg.bindingKey];
    if (binding && typeof binding === "object") {
      return rpcProxy<T>(binding);
    }
    return directProxy<T>(name, cfg.loadBundled);
  }

  const client: Record<string, object> = {};
  for (const [name, cfg] of Object.entries(MODULE_TRANSPORTS)) {
    client[name] = pick(
      name,
      cfg as ModuleTransportConfig<Record<string, unknown>>,
    );
  }

  return client as unknown as CrossModuleClient;
}

/**
 * Convenience alias used inside route handlers:
 *
 *   const cross = useCrossModule(c.env);
 *   await cross.accounting.createInvoice(db, companyId, data);
 */
export const useCrossModule = createCrossModuleClient;
